import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FlaskConical, Loader2 } from 'lucide-react';
import { useApp } from '@/context/AppContext';

export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const { handleOAuthCallback } = useApp();
  const [error, setError] = useState('');

  useEffect(() => {
    const processCallback = async () => {
      // Extract token from URL - try multiple methods for robustness
      // Method 1: Parse from hash (hash router: /#/auth/callback?token=xxx)
      let token: string | null = null;
      let provider = 'google';

      const hashParts = window.location.hash.split('?');
      if (hashParts.length > 1) {
        const hashParams = new URLSearchParams(hashParts[1]);
        token = hashParams.get('token');
        provider = hashParams.get('provider') || 'google';
      }

      // Method 2: Parse from search params (fallback)
      if (!token) {
        const searchParams = new URLSearchParams(window.location.search);
        token = searchParams.get('token');
        provider = searchParams.get('provider') || provider;
      }

      // Method 3: Parse from full href (last resort)
      if (!token) {
        const fullUrl = window.location.href;
        const tokenMatch = fullUrl.match(/[?&]token=([^&]+)/);
        const providerMatch = fullUrl.match(/[?&]provider=([^&]+)/);
        if (tokenMatch) token = decodeURIComponent(tokenMatch[1]);
        if (providerMatch) provider = providerMatch[1];
      }

      console.log('[AuthCallback] Token found:', !!token, 'Provider:', provider);

      if (!token) {
        // Check for error in URL
        const errorParam = window.location.hash.includes('error=') || window.location.search.includes('error=');
        setError(errorParam
          ? 'Authentication was denied or failed. Please try again.'
          : 'No authentication token received. Please try signing in again.');
        setTimeout(() => navigate('/login'), 3000);
        return;
      }

      try {
        const success = await handleOAuthCallback(token, provider);
        if (success) {
          navigate('/courses', { replace: true });
        } else {
          setError('Authentication failed. Please try again.');
          setTimeout(() => navigate('/login'), 3000);
        }
      } catch (err) {
        console.error('[AuthCallback] Error:', err);
        setError('Authentication error. Please try again.');
        setTimeout(() => navigate('/login'), 3000);
      }
    };

    processCallback();
  }, []);

  return (
    <div className="callback-container">
      <div className="callback-card">
        <div className="callback-icon">
          <FlaskConical size={32} />
        </div>
        {error ? (
          <>
            <h2>Authentication Error</h2>
            <p className="callback-error">{error}</p>
            <p className="callback-redirect">Redirecting to login...</p>
          </>
        ) : (
          <>
            <Loader2 size={32} className="spin" />
            <h2>Signing you in...</h2>
            <p>Please wait while we complete authentication.</p>
          </>
        )}
      </div>

      <style>{`
        .callback-container {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #0F0F13 0%, #1A1A24 50%, #13131A 100%);
        }
        .callback-card {
          text-align: center;
          padding: 48px;
        }
        .callback-icon {
          width: 64px;
          height: 64px;
          background: linear-gradient(135deg, #FF5A36, #00A86B);
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 24px;
          color: white;
        }
        .callback-card h2 {
          font-size: 20px;
          font-weight: 700;
          margin: 16px 0 8px;
          color: #FFFFFF;
        }
        .callback-card p {
          font-size: 14px;
          color: rgba(255,255,255,0.5);
          margin: 0;
        }
        .callback-error {
          color: #EF4444 !important;
        }
        .callback-redirect {
          margin-top: 12px !important;
          font-style: italic;
        }
        .spin {
          color: rgba(255,255,255,0.5);
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
